import React, {useEffect, useState, useRef} from 'react'
import {Redirect, useLocation} from 'react-router-dom'

import {
    Box,
    Spinner,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue, resolvePublicOrigin} from '../../utils/site-config'

/* Styles */
import {MyRewardsStylesProvider} from './styles/myRewardsStyles'

/* Hooks */
import { useLoyalty } from '../../integrations/loyalty/use-loyalty'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSafariDetection from '../../hooks/use-safari-detection'
import {preferenceToBool, useSitePreferences} from '../../hooks/use-site-preferences'
import {useOptimizely} from '../../contexts/optimizely-context'
import {useLoyaltyData} from '../../contexts/loyalty-data-context'
import {useHeaderContext} from '../../contexts/header-context'

/* Providers */
import {OptimizelyProvider} from '../../contexts/optimizely-context'
import {LoyaltyDataProvider} from '../../contexts/loyalty-data-context'

/* Components */
import ExclusiveRewardsBlock from './partials/exclusive-rewards-block'
import ExclusiveRewardsBlockCMS from './partials/exclusive-rewards-block-cms'
import {useIntl} from 'react-intl'
import Seo from '../../components/seo'
import {renderPagesSwitch} from '../../components/optimizely/utils/contentRenderer'
import {useBrand} from '../../contexts/brand-context'
import {getBrandDisplayName} from '../../utils/brandUtils'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'

const MyRewardsContent = () => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {rewardPageBlock, optimizelyData, isOptimizelyLoading} = useOptimizely()
    const cmsPage = optimizelyData?.item
    const {loyaltyData, isLoading: isLoyaltyDataLoading} = useLoyaltyData()
    const {data: customer} = useCurrentCustomer()
    const exclusiveRewardsRef = useRef(null)
    const [mounted, setMounted] = useState(false)
    const sflmEnabled = getSiteEnvVarValue(appConfig, 'SFLM_ENABLED', siteId)
    const {formatMessage} = useIntl()
    const {pathname, hash} = useLocation()
    const {headerHeight} = useHeaderContext()

    const {brandId} = useBrand()
    // Build the canonical from the configured public domain so the SSR canonical
    // matches the browser URL (behind the delivery proxy the request host is the
    // internal …exp-delivery.com origin).
    const appOrigin = resolvePublicOrigin(appConfig, siteId, brandId)
    const canonicalUrl = `${appOrigin}${pathname}`
    const brandDisplayName = getBrandDisplayName(brandId, site?.alias)

    const rewardsPageTitle = formatMessage(
        {
            id: 'rewards.page.title',
            defaultMessage: 'My Rewards | {brandId}'
        },
        {brandId: brandDisplayName}
    )

    const rewardsPageDescription = formatMessage(
        {
            id: 'rewards.page.description',
            defaultMessage: 'Track your Rewards and Loyalty Program at {brandId}.'
        },
        {brandId: brandDisplayName}
    )

    useEffect(() => {
        setMounted(true)
    }, [])

    // Anchor targets (e.g. #exclusive-rewards, #early-access) live inside content
    // that only mounts once loyalty/CMS data resolves, so the browser's native
    // scroll-on-load for the URL hash misses them. Scroll manually once ready,
    // offsetting by the sticky header height so it doesn't cover the section.
    useEffect(() => {
        if (!mounted || isOptimizelyLoading || isLoyaltyDataLoading || !hash) return
        const target = document.getElementById(hash.replace('#', ''))
        if (!target) return
        const top = target.getBoundingClientRect().top + window.scrollY - (headerHeight || 0) - 16
        window.scrollTo({top, behavior: 'smooth'})
    }, [mounted, isOptimizelyLoading, isLoyaltyDataLoading, hash, headerHeight])

    const seo = (
        <Seo
            title={rewardsPageTitle}
            description={rewardsPageDescription}
            canonicalUrl={canonicalUrl}
            openGraphTags={[
                {
                    property: 'og:title',
                    content: rewardsPageTitle
                },
                {
                    property: 'og:description',
                    content: rewardsPageDescription
                }
            ]}
        >
            <link rel="canonical" href={canonicalUrl} />
        </Seo>
    )

    if (!mounted) {
        return seo
    }

    if (isOptimizelyLoading && isLoyaltyDataLoading) {
        return (
            <>
                {seo}
                <Box
                    data-testid="error-page"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="60vh"
                    width="100%"
                >
                    <Spinner />
                </Box>
            </>
        )
    }

    return (
        <>
            {seo}
            {isHydrated() && (
                <Box data-testid="sf-my-rewards-container">
                    {/* CMS-driven content (MainRegion blocks rendered via renderPagesSwitch) */}
                    {cmsPage && renderPagesSwitch(cmsPage)}

                    {sflmEnabled && customer.isRegistered && (
                        <>
                            {/* Exclusive rewards block - only choose SFCC vs CMS after loyalty data has loaded to avoid CMS flashing then switching to SFCC */}
                            {!isLoyaltyDataLoading &&
                                (loyaltyData?.rewardsDataMapping ? (
                                    /* SFCC Content */
                                    <ExclusiveRewardsBlock
                                        ref={exclusiveRewardsRef}
                                        loyaltyData={loyaltyData}
                                        isLoyaltyDataLoading={isLoyaltyDataLoading}
                                    />
                                ) : (
                                    /* CMS Content only if we have configured */
                                    rewardPageBlock && (
                                        <ExclusiveRewardsBlockCMS
                                            ref={exclusiveRewardsRef}
                                            loyaltyData={loyaltyData}
                                            isLoyaltyDataLoading={isLoyaltyDataLoading}
                                        />
                                    )
                                ))}
                        </>
                    )}
                </Box>
            )}
        </>
    )
}

const MyRewards = () => {
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const {buildUrl} = useMultiSite()
    const { data: customer } = useCurrentCustomer()
    const isSafari = useSafariDetection()
    const styles = useMultiStyleConfig('MyRewards', {isSafari: isSafari})
    const {brandId} = useBrand()
    const rewardsPageIdentifier = brandId === 'dynamite' ? 'dyn-rewards-page' : 'grg-web-rewards-page'

    if (shouldHideLoyaltyUI) {
        return <Redirect to={buildUrl('/')} />
    }

    return (
        <OptimizelyProvider pageIdentifier={rewardsPageIdentifier}>
            <LoyaltyDataProvider>
                <MyRewardsStylesProvider value={styles}>
                    <MyRewardsContent />
                </MyRewardsStylesProvider>
            </LoyaltyDataProvider>
        </OptimizelyProvider>
    )
}

MyRewards.getTemplateName = () => 'my-rewards'

export default MyRewards
